"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { JOURNAL_TAGS } from "@/design-data/journal-data";
import type { ArticleRecord } from "@/server/modules/articles";

export function JournalGrid({ featured, posts }: { featured: ArticleRecord; posts: ArticleRecord[] }) {
  const [activeTag, setActiveTag] = useState<string>("All stories");

  const filtered = useMemo(() => {
    if (activeTag === "All stories") return posts;
    return posts.filter((p) => p.category === activeTag);
  }, [activeTag, posts]);

  return (
    <>
      <section className="pbd-container pt-16 pb-2">
        <p className="m-0 text-sm text-[var(--muted)]">
          <Link href="/" className="text-[var(--muted)] no-underline hover:text-[var(--accent)]">
            Home
          </Link>{" "}
          <span className="opacity-50">/</span>{" "}
          <span className="text-[var(--accent)]">Journal</span>
        </p>
        <h1 className="mt-3.5 mb-0 font-[family-name:var(--font-display)] text-[clamp(2.4rem,5vw,4rem)] font-medium leading-[1.02] tracking-tight">
          Field Notes &amp; Stories
        </h1>
        <p className="mt-4 mb-0 max-w-[620px] text-[1.05rem] leading-[1.7] text-[var(--muted)]">
          Dispatches from the road, culture, heritage, and the slow craft of travelling Pakistan gently.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {JOURNAL_TAGS.map((label) => {
            const on = activeTag === label;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setActiveTag(label)}
                className={`cursor-pointer rounded-full border px-3.5 py-2 text-sm font-semibold tracking-wide ${
                  on
                    ? "border-[var(--cta)] bg-[var(--cta)] text-[#2b2b2b]"
                    : "border-[var(--border-subtle)] bg-transparent text-[var(--muted)]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="pbd-container px-6 pt-9 pb-6">
        <Link
          href={`/journal/${featured.slug}`}
          className="group grid overflow-hidden rounded-[10px] border border-[var(--border-subtle)] text-[var(--foreground)] no-underline max-[980px]:grid-cols-1 lg:grid-cols-[1.3fr_1fr] hover:border-[color-mix(in_srgb,var(--accent)_45%,transparent)]"
        >
          <div className="relative min-h-[340px]">
            <Image src={featured.heroImage} alt={featured.title} fill className="object-cover" sizes="(max-width:980px) 100vw, 60vw" />
          </div>
          <div className="flex flex-col justify-center bg-[color-mix(in_srgb,var(--surface)_50%,transparent)] p-10">
            <p className="m-0 text-[11px] font-bold tracking-[0.18em] text-[var(--accent)] uppercase">
              {featured.category}
            </p>
            <h2 className="mt-3.5 mb-0 font-[family-name:var(--font-display)] text-[clamp(1.6rem,2.6vw,2.2rem)] leading-[1.15] font-medium">
              {featured.title}
            </h2>
            <p className="mt-3.5 mb-0 text-[15px] leading-[1.7] text-[var(--muted)]">{featured.excerpt}</p>
            <p className="mt-5 mb-0 text-[13px] text-[var(--muted)]">
              {featured.author} · {featured.readMinutes} min read
            </p>
          </div>
        </Link>
      </section>

      <section className="pbd-container px-6 pt-6 pb-20">
        <div className="grid gap-6 max-[640px]:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <Link key={p.slug} href={`/journal/${p.slug}`} className="group flex flex-col text-[var(--foreground)] no-underline">
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-[var(--border-subtle)] group-hover:border-[color-mix(in_srgb,var(--accent)_45%,transparent)]">
                <Image
                  src={p.heroImage}
                  alt={p.title}
                  fill
                  className="object-cover transition-transform duration-[600ms] group-hover:scale-105"
                  sizes="(max-width:640px) 100vw, 33vw"
                />
              </div>
              <p className="mt-3.5 mb-0 text-[11px] font-bold tracking-[0.16em] text-[var(--accent)] uppercase">{p.category}</p>
              <h3 className="mt-1.5 mb-0 font-[family-name:var(--font-display)] text-[1.3rem] leading-tight font-medium group-hover:text-[var(--accent)]">
                {p.title}
              </h3>
              <p className="mt-2 mb-0 text-[13px] leading-relaxed text-[var(--muted)]">{p.excerpt}</p>
              <p className="mt-3 mb-0 text-sm text-[var(--muted)]">{p.readMinutes} min read</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
